import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './VentasEnhanced.css';
import API from '../../utils/api';
import MapaMesas from './shared/MapaMesas';
import SeleccionMeseroHorizontal from './shared/selectors/SeleccionMeseroHorizontal';
import ModalNumeroPersonas from './shared/modals/ModalNumeroPersonas';
import PedidosLlevar from './pedidos/llevar/PedidoLlevar';
import PedidosUber from './pedidos/delivery/PedidosUber';
import PedidosRappi from './pedidos/delivery/PedidosRappi';
import PedidosDelibot from './pedidos/delivery/PedidosDelibot';
import PedidosDomicilio from './pedidos/domicilio/PedidosDomicilio';
import {
  ShoppingBag,
  Car,
  Bike,
  UtensilsCrossed,
  Home,
  RefreshCw,
  Users,
  X,
  Check,
  Calculator,
  Activity,
  Maximize2,
  Menu,
  ChefHat,
  Clock,
  Zap,
  Shield,
  Star
} from 'lucide-react';
import Header from '../common/Header';
import Footer from '../common/Footer';

// Configuración de colores legacy
const COLOR_MAP = {
  'clLime': '#32CD32', 'clRed': '#FF4136', 'clYellow': '#FFDC00', 'clBlue': '#0074D9',
  'clGreen': '#2ECC40', 'clBlack': '#111111', 'clWhite': '#FFFFFF', 'clGray': '#AAAAAA',
  'clSilver': '#DDDDDD', 'clMaroon': '#85144b', 'clOlive': '#3D9970', 'clPurple': '#B10DC9',
  'clTeal': '#39CCCC', 'clNavy': '#001f3f', 'clFuchsia': '#F012BE', 'clAqua': '#7FDBFF'
};

// Datos de fallback optimizados
const MOCK_MESAS = [];
const MOCK_MESEROS = [];

// Configuración de pestañas profesional mejorada
const TAB_CONFIG = [
  {
    id: 'mesa',
    label: 'SERVICIO EN MESA',
    icon: UtensilsCrossed,
    color: '#2563eb',
    shortLabel: 'MESA',
    gradient: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    description: 'Gestión de mesas y comensales'
  },
  {
    id: 'llevar',
    label: 'PARA LLEVAR',
    icon: ShoppingBag,
    color: '#059669',
    shortLabel: 'LLEVAR',
    gradient: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
    description: 'Pedidos para recoger'
  },
  {
    id: 'domicilio',
    label: 'SERVICIO A DOMICILIO',
    icon: Home,
    color: '#dc2626',
    shortLabel: 'DOMICILIO',
    gradient: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
    description: 'Entregas a domicilio'
  },
  {
    id: 'uber',
    label: 'UBER EATS',
    icon: Car,
    color: '#ea580c',
    shortLabel: 'UBER',
    gradient: 'linear-gradient(135deg, #ea580c 0%, #c2410c 100%)',
    description: 'Plataforma Uber Eats'
  },
  {
    id: 'rappi',
    label: 'RAPPI',
    icon: Zap,
    color: '#7c3aed',
    shortLabel: 'RAPPI',
    gradient: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
    description: 'Plataforma Rappi'
  },
  {
    id: 'delibot',
    label: 'DELIBOT',
    icon: Shield,
    color: '#0891b2',
    shortLabel: 'DELIBOT',
    gradient: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
    description: 'Plataforma Delibot'
  }
];

const Ventas = () => {
  const navigate = useNavigate();

  // Ref para controlar llamadas a la API
  const isLoadingMesas = useRef(false);

  // Estados principales optimizados
  const [activeTab, setActiveTab] = useState(() => {
    // Recuperar la pestaña activa desde localStorage
    const savedTab = localStorage.getItem('ventas-active-tab');
    // Verificar que la pestaña guardada sea válida
    const validTabs = ['mesa', 'llevar', 'domicilio', 'uber', 'rappi', 'delibot'];
    return savedTab && validTabs.includes(savedTab) ? savedTab : 'mesa';
  });
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mesas, setMesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Estados del flujo de selección
  const [mesaSeleccionada, setMesaSeleccionada] = useState(null);
  const [etapaActual, setEtapaActual] = useState('seleccionMesa');
  const [numeroPersonas, setNumeroPersonas] = useState('');
  const [meseros, setMeseros] = useState([]);
  const [meseroSeleccionado, setMeseroSeleccionado] = useState(null);
  const [loadingPedidoInfo, setLoadingPedidoInfo] = useState(false);
  const [pedidoExistente, setPedidoExistente] = useState(null);
  const [debeCrearPedido, setDebeCrearPedido] = useState(false);

  // Estados de UI mejorados
  const [showModalNumeroPersonas, setShowModalNumeroPersonas] = useState(false);
  const [showModalMesero, setShowModalMesero] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Configuración del sistema
  const systemConfig = API.auth.getSystemConfig() || {};
  const requiereNumeroPersonas = systemConfig.pedidoMesaSeleccionarNumero === '1' || systemConfig.pedidoMesaSeleccionarNumero === 1;
  const requiereMesero = systemConfig.pedidoMesaSeleccionarMesero === '1' || systemConfig.pedidoMesaSeleccionarMesero === 1 || true; // Por defecto siempre pedir mesero

  console.log('Configuración del sistema:', {
    systemConfig,
    requiereNumeroPersonas,
    requiereMesero
  });

  // Conversión de colores legacy optimizada
  const processLegacyColor = useCallback((color) => COLOR_MAP[color] || color, []);

  // Función optimizada para cargar mesas - CON CONTROL DE DUPLICADOS
  const fetchMesas = useCallback(async () => {
    // Evitar múltiples llamadas simultáneas
    if (isLoadingMesas.current) {
      console.log('⚠️ fetchMesas ya está ejecutándose, evitando llamada duplicada');
      return;
    }

    isLoadingMesas.current = true;

    try {
      setLoading(true);
      setError(null);

      if (typeof API.mesas?.getAll !== 'function') {
        throw new Error('API.mesas.getAll no disponible');
      }

      console.log('🔄 Cargando mesas desde API...');
      const mesasData = await API.mesas.getAll();
      const mesasProcesadas = mesasData.map(mesa => ({
        ...mesa,
        colorAbierta: processLegacyColor(mesa.colorAbierta),
        colorOcupada: processLegacyColor(mesa.colorOcupada),
        colorPendiente: processLegacyColor(mesa.colorPendiente)
      }));

      setMesas(mesasProcesadas);
      console.log('✅ Mesas cargadas exitosamente:', mesasProcesadas.length);
    } catch (err) {
      console.error("❌ Error cargando mesas:", err);
      setError("Error de conexión - usando datos demo");
      setMesas(MOCK_MESAS);
    } finally {
      setLoading(false);
      isLoadingMesas.current = false;
    }
  }, [processLegacyColor]);


  // Función optimizada para cargar meseros
  const fetchMeseros = useCallback(async () => {
    if (meseros.length > 0) return;

    try {
      setLoading(true);
      if (typeof API.meseros?.getAll !== 'function') {
        throw new Error('API.meseros.getAll no disponible');
      }

      const meserosData = await API.meseros.getAll();
      setMeseros(meserosData);
    } catch (err) {
      console.error("Error cargando meseros:", err);
      setMeseros(MOCK_MESEROS);
      setError(prev => prev || "Error de conexión - usando datos demo");
    } finally {
      setLoading(false);
    }
  }, [meseros.length]);

  // Reset del flujo optimizado - definido ANTES para evitar dependencias circulares
  const resetearFlujoNuevoPedido = useCallback(() => {
    setPedidoExistente(null);
    setEtapaActual('seleccionMesa');

    if (requiereNumeroPersonas) {
      setShowModalNumeroPersonas(true);
    } else if (requiereMesero) {
      setShowModalMesero(true);
    } else {
      setDebeCrearPedido(true);
    }
  }, [requiereNumeroPersonas, requiereMesero]);

  // Verificación de pedido existente optimizada
  const verificarPedidoExistente = useCallback(async (idMesa) => {
    setLoadingPedidoInfo(true);
    try {
      if (typeof API.pedidos?.verificarPedidoEnMesa !== 'function') {
        throw new Error('API.pedidos.verificarPedidoEnMesa no disponible');
      }

      const response = await API.pedidos.verificarPedidoEnMesa(idMesa);

      if (response.existePedido) {
        setPedidoExistente(response.pedido);
        response.pedido.numeroPersonas && setNumeroPersonas(response.pedido.numeroPersonas.toString());

        if (response.pedido.mesero) {
          setMeseroSeleccionado(response.pedido.mesero);
        }

        setEtapaActual('seleccionMesa');

        if (requiereNumeroPersonas && !response.pedido.numeroPersonas) {
          setShowModalNumeroPersonas(true);
        } else if (requiereMesero && !response.pedido.mesero) {
          setShowModalMesero(true);
        } else {
          setDebeCrearPedido(true);
        }
      } else {
        resetearFlujoNuevoPedido();
      }
    } catch (error) {
      console.error("Error verificando pedido:", error);
      resetearFlujoNuevoPedido();
    } finally {
      setLoadingPedidoInfo(false);
    }
  }, [requiereNumeroPersonas, requiereMesero, resetearFlujoNuevoPedido]);

  // Procedemiento de pedido optimizado
  const procederConPedido = useCallback(async () => {
    try {
      if (pedidoExistente) {
        const datosPedido = {
          idPedido: pedidoExistente.idPedido,
          numeroPedido: pedidoExistente.numero || pedidoExistente.numeroDia,
          numeroPersonas: numeroPersonas ? parseInt(numeroPersonas) : pedidoExistente.numeroPersonas,
          mesa: {
            idMesa: mesaSeleccionada.idMesa,
            numero: mesaSeleccionada.numero,
            nombre: mesaSeleccionada.nombre || `Mesa ${mesaSeleccionada.numero}`,
            caption: mesaSeleccionada.caption || mesaSeleccionada.nombre
          },
          mesero: meseroSeleccionado || pedidoExistente.mesero,
          status: pedidoExistente.status,
          pedidoExistente: pedidoExistente // Mantener referencia completa por compatibilidad
        };

        // Navegar con datos del pedido como state
        navigate(`/mesa-detalle/${mesaSeleccionada.idMesa}`, { state: { datosPedido } });
        return;
      }

      const usuario = API.auth.getCurrentUser() || { nombre: 'Admin' };
      console.log("usuariousuario", usuario)

      const nuevoPedido = {
        idMesa: mesaSeleccionada.idMesa,
        numeroMesa: mesaSeleccionada.numero,
        nombreMesa: mesaSeleccionada.nombre || `Mesa_${mesaSeleccionada.numero}`,
        numeroPersonas: numeroPersonas ? parseInt(numeroPersonas) : 1,
        idMesero: meseroSeleccionado ? meseroSeleccionado.idMesero : null,
        nombreMesero: meseroSeleccionado ? meseroSeleccionado.nombre : null,
        usuario: usuario.usuario || 'Admin'
      };

      setLoadingPedidoInfo(true);

      if (typeof API.pedidos?.crearPedido !== 'function') {
        throw new Error('API.pedidos.crearPedido no disponible');
      }

      const pedidoCreado = await API.pedidos.crearPedido(nuevoPedido);

      const datosPedido = {
        idPedido: pedidoCreado.idPedido,
        numeroPedido: pedidoCreado.numero,
        numeroPersonas: numeroPersonas ? parseInt(numeroPersonas) : 1,
        mesa: {
          idMesa: mesaSeleccionada.idMesa,
          numero: mesaSeleccionada.numero,
          nombre: mesaSeleccionada.nombre || `Mesa ${mesaSeleccionada.numero}`,
          caption: mesaSeleccionada.caption || mesaSeleccionada.nombre
        },
        mesero: meseroSeleccionado,
        status: pedidoCreado.status || 'abierto',
        pedidoCreado: pedidoCreado // Mantener referencia completa por compatibilidad
      };

      // Navegar con datos del pedido como state
      navigate(`/mesa-detalle/${mesaSeleccionada.idMesa}`, { state: { datosPedido } });
    } catch (error) {
      console.error("Error creando pedido:", error);
      alert("Error al crear el pedido. Inténtelo de nuevo.");
    } finally {
      setLoadingPedidoInfo(false);
    }
  }, [mesaSeleccionada, numeroPersonas, meseroSeleccionado, pedidoExistente, navigate]);

  // Cargar mesas al montar el componente - SOLO UNA VEZ
  useEffect(() => {
    console.log('🚀 Componente Ventas montado, cargando mesas iniciales...');
    fetchMesas();
  }, []); // Array vacío = solo al montar

  // Efectos optimizados para evitar bucles infinitos
  useEffect(() => {
    if (activeTab === 'mesa' && mesas.length === 0 && !isLoadingMesas.current) {
      console.log('📋 Tab mesa activado pero sin mesas, cargando...');
      fetchMesas();
    }
  }, [activeTab]); // REMOVIDO fetchMesas de dependencias

  useEffect(() => {
    if (showModalMesero) {
      fetchMeseros();
    }
  }, [showModalMesero]); // REMOVIDO fetchMeseros de dependencias

  useEffect(() => {
    if (mesaSeleccionada && etapaActual === 'verificarPedido') {
      verificarPedidoExistente(mesaSeleccionada.idMesa);
    }
  }, [mesaSeleccionada, etapaActual]); // REMOVIDO verificarPedidoExistente de dependencias

  useEffect(() => {
    if (debeCrearPedido && mesaSeleccionada) {
      procederConPedido();
      setDebeCrearPedido(false);
    }
  }, [debeCrearPedido, mesaSeleccionada]); // REMOVIDO procederConPedido de dependencias


  // Manejadores de eventos optimizados
  const handleMesaClick = useCallback((mesa) => {
    setMesaSeleccionada(mesa);
    setEtapaActual('verificarPedido');
  }, []);

  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab);
    // Guardar la pestaña activa en localStorage
    localStorage.setItem('ventas-active-tab', tab);
    setEtapaActual('seleccionMesa');
    setMesaSeleccionada(null);
    setNumeroPersonas('');
    setMeseroSeleccionado(null);
    setShowModalNumeroPersonas(false);
    setShowModalMesero(false);
    setDebeCrearPedido(false);
  }, []);


  const confirmarNumeroPersonas = useCallback((numero = null) => {
    const numeroFinal = numero || parseInt(numeroPersonas);

    if (!numeroFinal || numeroFinal <= 0) {
      alert("Ingrese un número válido de personas");
      return;
    }

    setNumeroPersonas(numeroFinal.toString());
    setShowModalNumeroPersonas(false);

    if (requiereMesero) {
      // Añadir un pequeño delay para asegurar que el modal anterior se cierre
      setTimeout(() => {
        setShowModalMesero(true);
      }, 100);
    } else {
      setDebeCrearPedido(true);
    }
  }, [numeroPersonas, requiereMesero]);

  const handleMeseroSeleccionado = useCallback((mesero) => {
    if (mesero && typeof mesero === 'object') {
      setMeseroSeleccionado(mesero);
      setShowModalMesero(false);
      setDebeCrearPedido(true);
    } else {
      console.error("Error: formato de mesero inválido", mesero);
      alert("Error al seleccionar mesero. Inténtelo de nuevo.");
      setShowModalMesero(false);
    }
  }, []);

  const handleCancelarSeleccionMesero = useCallback(() => {
    setShowModalMesero(false);
    if (requiereNumeroPersonas) {
      setShowModalNumeroPersonas(true);
    } else {
      setMesaSeleccionada(null);
    }
  }, [requiereNumeroPersonas]);

  const handleCancelarNumeroPersonas = useCallback(() => {
    setShowModalNumeroPersonas(false);
    setMesaSeleccionada(null);
  }, []);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(!isFullscreen);
  }, [isFullscreen]);

  const toggleSidebar = useCallback(() => {
    setSidebarCollapsed(!sidebarCollapsed);
  }, [sidebarCollapsed]);



  // Renderizado de tabs profesional mejorado
  const renderTabs = () => (
    <div className="enhanced-tabs-professional">



      <div className={`tabs-container-professional ${sidebarCollapsed ? 'collapsed' : ''}`}>
        <div className="tabs-navigation">
          {TAB_CONFIG.map(tab => {
            const IconComponent = tab.icon;
            const isActive = activeTab === tab.id;

            return (
              <button
                key={tab.id}
                className={`professional-tab-button ${isActive ? 'active' : ''}`}
                onClick={() => handleTabChange(tab.id)}
                style={{
                  '--tab-color': tab.color,
                  '--tab-gradient': tab.gradient
                }}
                title={tab.description}
              >
                <div className="tab-icon-professional">
                  <IconComponent size={sidebarCollapsed ? 22 : 20} />
                  {isActive && <div className="active-indicator" />}
                </div>

                {!sidebarCollapsed && (
                  <div className="tab-content-professional">
                    <span className="tab-label-professional">{tab.label}</span>
                    <span className="tab-description">{tab.description}</span>
                  </div>
                )}

                {!sidebarCollapsed && (
                  <div className="tab-status">
                    <Star size={14} className="status-icon" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // Renderizado de etapa actual mejorado y profesional
  const renderEtapaActual = useCallback(() => {
    switch (etapaActual) {
      case 'seleccionMesa':
        return (
          <div className="main-content-professional">
            <div className="content-header-professional">
              <div className="header-info">
                <div className="page-title-section">
                  <UtensilsCrossed size={24} className="page-icon" />
                  <div className="title-info">
                    <h2 className="page-title">Gestión de Mesas</h2>
                    <p className="page-subtitle">Seleccione una mesa para gestionar pedidos</p>
                  </div>
                </div>
              </div>

              <div className="header-actions-professional">
                <div className="status-indicators">
                  <div className="status-badge available">
                    <span className="status-dot"></span>
                    Disponible
                  </div>
                  <div className="status-badge occupied">
                    <span className="status-dot"></span>
                    Ocupada
                  </div>
                  <div className="status-badge pending">
                    <span className="status-dot"></span>
                    Pendiente
                  </div>
                </div>

                <button
                  className="refresh-button-professional"
                  onClick={fetchMesas}
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                  <span>{loading ? 'Actualizando...' : 'Actualizar'}</span>
                </button>
              </div>
            </div>

            {loading && mesas.length === 0 ? (
              <div className="loading-state-professional">
                <div className="loading-content">
                  <div className="loading-animation">
                    <Activity size={40} className="loading-icon" />
                    <div className="loading-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                  <h3>Cargando información de mesas</h3>
                  <p>Obteniendo el estado actual del restaurante...</p>
                </div>
              </div>
            ) : (
              <div className="mesas-container-professional">
                <div className="mesas-grid-wrapper">
                  <MapaMesas
                    mesas={mesas}
                    onMesaClick={handleMesaClick}
                  />
                </div>
              </div>
            )}

            {error && (
              <div className="error-banner-professional">
                <div className="error-content">
                  <X size={20} />
                  <span>{error}</span>
                </div>
              </div>
            )}
          </div>
        );

      case 'verificarPedido':
        return (
          <div className="verification-state-professional">
            <div className="verification-card">
              <div className="verification-header">
                <Calculator size={48} className="verification-icon" />
                <div className="verification-content">
                  <h3>Procesando Mesa {mesaSeleccionada?.nombre || mesaSeleccionada?.numero}</h3>
                  <p>Verificando el estado actual del pedido...</p>
                </div>
              </div>

              <div className="verification-progress">
                <div className="progress-bar">
                  <div className="progress-fill"></div>
                </div>
                <span className="progress-text">Consultando base de datos...</span>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="error-state-professional">
            <div className="error-content">
              <X size={48} className="error-icon" />
              <h3>Estado no reconocido</h3>
              <p>Ha ocurrido un error inesperado en la aplicación.</p>
            </div>
          </div>
        );
    }
  }, [etapaActual, loading, mesas, error, mesaSeleccionada, handleMesaClick, fetchMesas]);

  // Renderizado principal mejorado y profesional
  return (
    <div className={`professional-dashboard ${isFullscreen ? 'fullscreen' : ''}`}>
      <Header titulo="PRESTO - Sistema de Ventas" color="#2563eb" />

      <div className="dashboard-layout-professional">
        {/* Panel lateral de navegación profesional */}
        <aside className={`sidebar-professional ${sidebarCollapsed ? 'collapsed' : ''}`}>
          {renderTabs()}
        </aside>

        {/* Contenido principal */}
        <main className="main-area-professional">
          <div className="content-wrapper-professional">
            {activeTab === 'mesa' && renderEtapaActual()}

            {activeTab === 'llevar' && (
              <div className="tab-content-wrapper-professional">
                <div className="content-header-tab">
                  <div className="tab-header-info">
                    <ShoppingBag size={24} className="tab-icon-header" />
                    <div className="tab-title-section">
                      <h2>Pedidos Para Llevar</h2>
                      <p>Gestión de órdenes para recoger en el local</p>
                    </div>
                  </div>
                </div>
                <div className="tab-content-body">
                  <PedidosLlevar />
                </div>
              </div>
            )}

            {activeTab === 'uber' && (
              <div className="tab-content-wrapper-professional">
                <div className="content-header-tab">
                  <div className="tab-header-info">
                    <Car size={24} className="tab-icon-header" />
                    <div className="tab-title-section">
                      <h2>Uber Eats</h2>
                      <p>Gestión de pedidos de la plataforma Uber Eats</p>
                    </div>
                  </div>
                </div>
                <div className="tab-content-body">
                  <PedidosUber />
                </div>
              </div>
            )}

            {activeTab === 'rappi' && (
              <div className="tab-content-wrapper-professional">
                <div className="content-header-tab">
                  <div className="tab-header-info">
                    <Zap size={24} className="tab-icon-header" />
                    <div className="tab-title-section">
                      <h2>Rappi</h2>
                      <p>Gestión de pedidos de la plataforma Rappi</p>
                    </div>
                  </div>
                </div>
                <div className="tab-content-body">
                  <PedidosRappi />
                </div>
              </div>
            )}

            {activeTab === 'delibot' && (
              <div className="tab-content-wrapper-professional">
                <div className="content-header-tab">
                  <div className="tab-header-info">
                    <Shield size={24} className="tab-icon-header" />
                    <div className="tab-title-section">
                      <h2>Delibot</h2>
                      <p>Gestión de pedidos de la plataforma Delibot</p>
                    </div>
                  </div>
                </div>
                <div className="tab-content-body">
                  <PedidosDelibot />
                </div>
              </div>
            )}

            {activeTab === 'domicilio' && (
              <div className="tab-content-wrapper-professional">
                <div className="content-header-tab">
                  <div className="tab-header-info">
                    <Home size={24} className="tab-icon-header" />
                    <div className="tab-title-section">
                      <h2>Servicio a Domicilio</h2>
                      <p>Gestión de entregas directas del restaurante</p>
                    </div>
                  </div>
                </div>
                <div className="tab-content-body">
                  <PedidosDomicilio />
                </div>
              </div>
            )}
          </div>
        </main>
      </div>

      {/* Modal de Número de Personas Mejorado */}
      <ModalNumeroPersonas
        isOpen={showModalNumeroPersonas}
        onConfirm={confirmarNumeroPersonas}
        onCancel={handleCancelarNumeroPersonas}
        initialValue={parseInt(numeroPersonas) || ''}
        title="Número de Personas"
        label="Personas en la mesa"
      />

      {/* Modal de Mesero Mejorado */}
      {showModalMesero && (
        <SeleccionMeseroHorizontal
          onMeseroSelect={handleMeseroSeleccionado}
          onCancel={handleCancelarSeleccionMesero}
          meseroActual={meseroSeleccionado}
        />
      )}

      {/* Overlay de carga global profesional */}
      {loadingPedidoInfo && (
        <div className="professional-loading-overlay">
          <div className="loading-card-professional">
            <div className="loading-animation-professional">
              <Calculator size={48} className="loading-icon-pro" />
              <div className="loading-spinner-pro">
                <div className="spinner-inner"></div>
              </div>
            </div>
            <div className="loading-content-pro">
              <h3>Procesando Pedido</h3>
              <p>Configurando su pedido, por favor espere un momento...</p>
              <div className="loading-progress-bar">
                <div className="progress-fill-pro"></div>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Ventas;